import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { getTradingPlan, saveTradingPlan } from '@/utils/localStorage';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, AlertTriangle } from 'lucide-react';

export default function MyTradingPlan() {
  const [plan, setPlan] = useState({
    enabled: false,
    maxTradesPerDay: '',
    minRiskReward: '',
    maxRiskPerTrade: '',
    dailyLossLimit: '',
    allowedSetupTypes: [],
    tradingSessions: []
  });
  
  const [newSession, setNewSession] = useState({ start: '', end: '' });
  
  useEffect(() => {
    const savedPlan = getTradingPlan();
    if (savedPlan) {
      setPlan(savedPlan);
    }
  }, []);
  
  const handleSave = () => {
    try {
      saveTradingPlan(plan);
      toast.success('Trading plan saved successfully');
    } catch (error) {
      toast.error('Failed to save trading plan');
    }
  };
  
  const handleSetupTypeToggle = (type) => {
    setPlan(prev => {
      const current = prev.allowedSetupTypes || [];
      if (current.includes(type)) {
        return { ...prev, allowedSetupTypes: current.filter(t => t !== type) };
      } else {
        return { ...prev, allowedSetupTypes: [...current, type] };
      }
    });
  };
  
  const addTradingSession = () => {
    if (newSession.start && newSession.end) {
      setPlan(prev => ({
        ...prev,
        tradingSessions: [...(prev.tradingSessions || []), { ...newSession }]
      }));
      setNewSession({ start: '', end: '' });
    } else {
      toast.error('Please enter both start and end times');
    }
  };
  
  const removeSession = (index) => {
    setPlan(prev => ({
      ...prev,
      tradingSessions: prev.tradingSessions.filter((_, i) => i !== index)
    }));
  };
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <Shield className="w-10 h-10 text-primary" />
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
            My Trading Plan
          </h1>
        </div>
        <p className="text-muted-foreground font-medium">
          Define your personal trading rules. The app will enforce discipline and prevent rule violations.
        </p>
      </div>
      
      <div className="mb-6">
        <Card className="p-6 border-2 border-primary/30 bg-primary/5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-foreground mb-1">Trading Plan Status</h3>
              <p className="text-sm text-muted-foreground">
                {plan.enabled ? 'Your trading plan is active and enforcing rules' : 'Trading plan is disabled - no rules being enforced'}
              </p>
            </div>
            <Switch
              data-testid="plan-enabled-toggle"
              checked={plan.enabled}
              onCheckedChange={(checked) => setPlan(prev => ({ ...prev, enabled: checked }))}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </Card>
      </div>
      
      {!plan.enabled && (
        <Card className="p-6 border-destructive/50 bg-destructive/10 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-destructive mb-1">Trading Plan Disabled</h4>
              <p className="text-sm text-foreground">
                Enable your trading plan to enforce rules and improve discipline. Without an active plan, you're trading without guardrails.
              </p>
            </div>
          </div>
        </Card>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border/40 bg-card">
          <h2 className="text-xl font-bold text-foreground mb-4">Risk Management Rules</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="maxTradesPerDay" className="text-foreground font-semibold mb-2 block">
                Max Trades Per Day
              </Label>
              <Input
                id="maxTradesPerDay"
                data-testid="max-trades-input"
                type="number"
                min="0"
                value={plan.maxTradesPerDay}
                onChange={(e) => setPlan(prev => ({ ...prev, maxTradesPerDay: e.target.value }))}
                placeholder="e.g., 3"
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Prevents overtrading. Leave empty for no limit.</p>
            </div>
            
            <div>
              <Label htmlFor="minRiskReward" className="text-foreground font-semibold mb-2 block">
                Minimum Risk:Reward Ratio
              </Label>
              <Input
                id="minRiskReward"
                data-testid="min-rr-input"
                type="number"
                step="0.1"
                min="0"
                value={plan.minRiskReward}
                onChange={(e) => setPlan(prev => ({ ...prev, minRiskReward: e.target.value }))}
                placeholder="e.g., 2.0"
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Only take trades with favorable risk/reward. Recommended: 1.5 or higher.</p>
            </div>
            
            <div>
              <Label htmlFor="maxRiskPerTrade" className="text-foreground font-semibold mb-2 block">
                Max Risk Per Trade (%)
              </Label>
              <Input
                id="maxRiskPerTrade"
                data-testid="max-risk-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={plan.maxRiskPerTrade}
                onChange={(e) => setPlan(prev => ({ ...prev, maxRiskPerTrade: e.target.value }))}
                placeholder="e.g., 2.0"
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Maximum % of account to risk on a single trade.</p>
            </div>
            
            <div>
              <Label htmlFor="dailyLossLimit" className="text-foreground font-semibold mb-2 block">
                Daily Loss Limit (%)
              </Label>
              <Input
                id="dailyLossLimit"
                data-testid="daily-loss-input"
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={plan.dailyLossLimit}
                onChange={(e) => setPlan(prev => ({ ...prev, dailyLossLimit: e.target.value }))}
                placeholder="e.g., 5.0"
                className="h-11 bg-secondary/20 border-border/50 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">Stop trading after losing this % in a single day.</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-6 border-border/40 bg-card">
          <h2 className="text-xl font-bold text-foreground mb-4">Allowed Setup Types</h2>
          
          <div className="space-y-3 mb-6">
            {['breakout', 'pullback', 'reversal'].map(type => (
              <div key={type} className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                <Label htmlFor={`setup-${type}`} className="text-foreground font-medium cursor-pointer flex-1 capitalize">
                  {type}
                </Label>
                <Checkbox
                  id={`setup-${type}`}
                  data-testid={`setup-type-${type}`}
                  checked={plan.allowedSetupTypes?.includes(type)}
                  onCheckedChange={() => handleSetupTypeToggle(type)}
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Only trade your best setups. Uncheck types you want to avoid.</p>
        </Card>
      </div>
      
      <Card className="p-6 border-border/40 bg-card mt-6">
        <h2 className="text-xl font-bold text-foreground mb-4">Allowed Trading Sessions</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Define specific time windows when you're allowed to trade. This prevents impulsive trading outside your optimal hours.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex-1">
            <Label htmlFor="sessionStart" className="text-sm font-semibold mb-2 block">Start Time</Label>
            <Input
              id="sessionStart"
              data-testid="session-start-input"
              type="time"
              value={newSession.start}
              onChange={(e) => setNewSession(prev => ({ ...prev, start: e.target.value }))}
              className="h-11 bg-secondary/20 border-border/50"
            />
          </div>
          
          <div className="flex-1">
            <Label htmlFor="sessionEnd" className="text-sm font-semibold mb-2 block">End Time</Label>
            <Input
              id="sessionEnd"
              data-testid="session-end-input"
              type="time"
              value={newSession.end}
              onChange={(e) => setNewSession(prev => ({ ...prev, end: e.target.value }))}
              className="h-11 bg-secondary/20 border-border/50"
            />
          </div>
          
          <div className="flex items-end">
            <Button
              onClick={addTradingSession}
              data-testid="add-session-btn"
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-11"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
        
        {plan.tradingSessions && plan.tradingSessions.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-foreground mb-2">Active Sessions</h3>
            {plan.tradingSessions.map((session, index) => (
              <div
                key={index}
                data-testid={`session-${index}`}
                className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30"
              >
                <span className="font-mono font-semibold text-foreground">
                  {session.start} - {session.end}
                </span>
                <Button
                  onClick={() => removeSession(index)}
                  data-testid={`remove-session-${index}`}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-destructive/20 hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        {(!plan.tradingSessions || plan.tradingSessions.length === 0) && (
          <p className="text-sm text-muted-foreground italic">No trading sessions defined. You can trade at any time.</p>
        )}
      </Card>
      
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          data-testid="save-plan-btn"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 h-12 text-lg"
        >
          <Shield className="w-5 h-5 mr-2" />
          Save Trading Plan
        </Button>
      </div>
    </div>
  );
}